import { useState } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { Button } from "react-admin";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { SectionTitle } from "features/SectionTitle";
import { useNotifyError } from "utils/useNotifyError";
import { useCurriculum } from "../CurriculumContext";
import { useCollectionMutations } from "./useCollectionMutations";

const AffectedYearsEditor = ({ years, savedYears, saving, onSave }) => {
  const [selected, setSelected] = useState(savedYears);

  const toggle = year =>
    setSelected(current => (current.includes(year) ? current.filter(y => y !== year) : [...current, year]));

  const isDirty = selected.length !== savedYears.length || selected.some(year => !savedYears.includes(year));

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {years.map(year => (
          <FormControlLabel
            key={year}
            label={String(year)}
            labelPlacement="top"
            control={
              <Checkbox
                checked={selected.includes(year)}
                onChange={() => toggle(year)}
                disabled={saving}
              />
            }
          />
        ))}
      </Box>

      <Button
        label="Запази"
        variant="contained"
        disabled={saving || !isDirty}
        onClick={() => onSave([...selected].sort((a, b) => a - b).map(yearOfStudy => ({ yearOfStudy })))}
      />
    </>
  );
};

// Not a grid like the other collections — a tick per year of study, saved as one list.
const AffectedYearsSection = ({ collection }) => {
  const curriculum = useCurriculum();
  const { items, saving, replaceAll } = useCollectionMutations(curriculum, collection);
  const notifyError = useNotifyError();

  // Saved straight from a button, so no modal is there to report a failure.
  const saveYears = async next => {
    try {
      await replaceAll(next, "Засегнатите години са запазени успешно");
    } catch (err) {
      notifyError(err);
    }
  };

  const yearsOfStudy = Math.ceil(curriculum?.yearsOfStudy ?? 0);
  const savedYears = items.map(item => item.yearOfStudy).sort((a, b) => a - b);
  // Years saved before the plan was shortened stay tickable so they can be removed.
  const orphanYears = savedYears.filter(year => year > yearsOfStudy);
  const years = [...Array.from({ length: yearsOfStudy }, (_, index) => index + 1), ...orphanYears];

  if (!yearsOfStudy) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        Въведете срок на обучение, за да изберете засегнати години.
      </Typography>
    );
  }

  return (
    <>
      <SectionTitle
        title={collection.label}
        paddingY={0}
      />
      <SecuredComponent permission={permissions.canUpdateCurriculums}>
        <AffectedYearsEditor
          // Remounts on the persisted set so the ticks follow the record after a save.
          key={savedYears.join(",")}
          years={years}
          savedYears={savedYears}
          saving={saving}
          onSave={saveYears}
        />
      </SecuredComponent>
    </>
  );
};

export default AffectedYearsSection;
